'use strict';

/**
 * NACRE Wave 2.1.4 — hybrid key exchange (X25519 + ML-KEM-768)
 *
 * Hybrid post-quantum key encapsulation. Used to seal a per-message session
 * key from sender to recipient when delivering an encrypted message, file,
 * or any other ciphertext through the platform.
 *
 * The hybrid construction combines two independent key-exchange primitives
 * and uses HKDF over their outputs to produce the final shared secret. An
 * attacker must break BOTH primitives to recover the secret. As of 2026:
 *
 *   - X25519 is broken by sufficiently large quantum computers (Shor's
 *     algorithm) but is fast, well-tested, and the classical workhorse
 *   - ML-KEM-768 (NIST FIPS 203) is the standardized lattice-based KEM
 *     and is believed to resist quantum attacks
 *
 * Combining them gives "harvest-now-decrypt-later" resistance: even if a
 * quantum adversary records today's traffic and runs Shor in 2035, they
 * would also need a working attack on lattice crypto to read the message.
 *
 * Output of encapsulate(): a tuple
 *   {
 *     kemCiphertext:           ML-KEM-768 ciphertext (1088 bytes)
 *     x25519EphemeralPublic:   sender's ephemeral X25519 public key (32 bytes)
 *     sharedSecret:            32-byte HKDF-derived combined secret
 *     kexVersion:              the combiner version this was sealed under
 *                              (algo-versions KEX_CURRENT) — stamp it in the
 *                              envelope; decapsulate needs it back
 *   }
 *
 * The recipient stores their long-term hybrid keypair and decapsulates by
 * (a) running ML-KEM decapsulation on kemCiphertext, (b) running X25519
 * ECDH against the ephemeral public, (c) HKDF-combining both — together with
 * the transcript — into the 32-byte shared secret.
 *
 * The 32-byte shared secret is suitable for use as an AES-256 key for the
 * symmetric encryption of the message body.
 *
 * ── T513 / S95 (2026-09-05) — TRANSCRIPT BINDING (X-Wing-style), KEX v2 ──
 *
 * The v1 combiner derived HKDF(ss_x25519 ‖ ss_mlkem, "oyster:kex:hybrid:v1").
 * That is a proper KDF over BOTH secrets (the break-both guarantee held) but
 * it did NOT COMMIT to the transcript: kemCiphertext and x25519EphemeralPublic
 * were returned and never entered the KDF. ML-KEM and raw X25519 are
 * individually non-committing (Cremers et al. 2024 — ML-KEM lacks
 * MAL-BIND-K-CT/PK), so two distinct transcripts could derive ONE key.
 * Measured (docs/audit/T513-verify-hybrid-kex-transcript-binding/lane.md): an
 * RFC 7748 ignored-bit twin ephemeral (byte 31 bit 7) is a distinct wire value
 * decoding to the same field element, and v1 derived the IDENTICAL secret for
 * both with no refusal.
 *
 * v2 (X-Wing, draft-connolly-cfrg-xwing-kem; CFRG draft-ietf-tls-hybrid-design):
 *   SS = HKDF( ss_x25519 ‖ ss_mlkem ‖ kemCiphertext ‖ x25519EphemeralPublic
 *              ‖ recipientPublicKey.x25519 ‖ recipientPublicKey.mlKem,
 *              label = "oyster:kex:hybrid:v2", 32 )
 * Every field is fixed-width (32‖32‖1088‖32‖32‖1184 bytes) so the
 * concatenation is injective — no length confusion between fields. The
 * recipient public key is bound on the decapsulating side by deriving it from
 * the private key (X25519 scalar-mult; the ML-KEM decapsulation key embeds its
 * encapsulation key per FIPS 203 §7.3), so the API is unchanged. decapsulate
 * additionally REFUSES a non-canonical ephemeral (bit 255 set) explicitly.
 *
 * VERSIONING — ONE place. The algo-version string stamped on stored blobs
 * (algo-versions.js KEX_V1 / KEX_V2) and the HKDF label the combiner derives
 * under are BOTH owned by algo-versions.js (`kexHkdfLabel(version)`); this
 * file never spells a label. New encapsulations always emit KEX_CURRENT (v2)
 * and stamp it in the result. decapsulate takes the version as its 4th
 * argument (default KEX_CURRENT); v1 is a DEPRECATED read-only path retained
 * so at-rest envelopes sealed before T513 stay readable — the retired form is
 * quoted at combineSharedSecret. The at-rest re-seal sweep is a USER-ACTION
 * (scripts/t513-kex-v1-envelope-census.js is report-only).
 *
 * ⛔ ANTI-FIX (plan): binding the transcript only in a consumer's AEAD
 * associated data (seal-box.js) or envelope layer (hybrid-envelope.js) does
 * NOT close this — the combiner itself must bind, so every consumer inherits
 * it. Do not "simplify" v2 back to secrets-only under a new label.
 */

const { x25519 } = require('@noble/curves/ed25519.js');
const { ml_kem768 } = require('@noble/post-quantum/ml-kem.js');
const { hkdfSubkey } = require('./kdf');
const { KEX_V1, KEX_V2, KEX_CURRENT, kexHkdfLabel } = require('./algo-versions');
const crypto = require('crypto');

const KEY_SIZES = Object.freeze({
  x25519PublicKey: 32,
  x25519PrivateKey: 32,
  mlKemPublicKey: 1184,
  mlKemPrivateKey: 2400,
  kemCiphertext: 1088,
  sharedSecret: 32,
});

// Frozen allowlist of combiner versions this build can DERIVE under. v1 is
// read-only (see combineSharedSecret); KEX_CURRENT is the only write version.
const KEX_VERSIONS = Object.freeze([KEX_V1, KEX_V2]);

// FIPS 203 §7.3 — ML-KEM decapsulation key layout:
//   dk_PKE (384·k = 1152 B for k=3) ‖ ek (1184 B) ‖ H(ek) (32 B) ‖ z (32 B) = 2400 B
// The encapsulation key is embedded at a fixed offset, which is what lets
// decapsulate bind the recipient public key into the v2 transcript with no API
// change (X-Wing derives pk from sk the same way). @noble/post-quantum encodes
// exactly this ([sk, publicKey, publicKeyHash, z]) and its own decapsulate()
// slices ek back out at the same offset.
const ML_KEM_EK_OFFSET = 1152;

const ERR_KEX_NONCANONICAL_EPHEMERAL = 'ERR_KEX_NONCANONICAL_EPHEMERAL';
const ERR_KEX_VERSION_UNSUPPORTED = 'ERR_KEX_VERSION_UNSUPPORTED';

class KexError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'KexError';
    this.code = code;
  }
}

function concat(parts) {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
}

function assertBytes(value, length, name) {
  if (!(value instanceof Uint8Array) || value.length !== length) {
    throw new Error('Invalid ' + name + ': must be ' + length + ' bytes');
  }
}

function assertKexVersion(version) {
  if (!KEX_VERSIONS.includes(version)) {
    throw new KexError(
      ERR_KEX_VERSION_UNSUPPORTED,
      'Unsupported KEX version: ' + String(version) + ' (supported: ' + KEX_VERSIONS.join(', ') + ')',
    );
  }
}

/**
 * RFC 7748 §5: the receiver MUST mask the most significant bit of the final
 * byte on decode, so `eph` and `eph XOR (1<<255)` are two distinct 32-byte
 * wire values that decode to ONE field element. A canonical encoding never
 * has the bit set (u < 2^255 − 19) and this module's encapsulate never emits
 * one, so a set bit is a forged/malleated transcript: refuse it explicitly
 * rather than letting the curve silently collapse it. (Other non-canonical
 * encodings, p ≤ u < 2^255, are distinct wire values that v2 binds into the
 * KDF — a different transcript derives a different key.)
 */
function assertCanonicalEphemeral(x25519EphemeralPublic) {
  if (x25519EphemeralPublic[31] & 0x80) {
    throw new KexError(
      ERR_KEX_NONCANONICAL_EPHEMERAL,
      'Invalid x25519EphemeralPublic: non-canonical encoding (bit 255 set) refused',
    );
  }
}

/** ML-KEM-768 encapsulation key, sliced from the decapsulation key (FIPS 203 §7.3). */
function mlKemPublicFromPrivate(mlKemPrivateKey) {
  return mlKemPrivateKey.subarray(ML_KEM_EK_OFFSET, ML_KEM_EK_OFFSET + KEY_SIZES.mlKemPublicKey);
}

/**
 * Generate a fresh hybrid keypair (X25519 + ML-KEM-768).
 *
 * @returns {{publicKey: {x25519: Uint8Array, mlKem: Uint8Array},
 *           privateKey: {x25519: Uint8Array, mlKem: Uint8Array}}}
 */
function generateKeyPair() {
  const x25519Priv = x25519.utils.randomSecretKey();
  const x25519Pub = x25519.getPublicKey(x25519Priv);
  const mlKemKp = ml_kem768.keygen();
  return {
    publicKey: {
      x25519: x25519Pub,
      mlKem: mlKemKp.publicKey,
    },
    privateKey: {
      x25519: x25519Priv,
      mlKem: mlKemKp.secretKey,
    },
  };
}

function validateHybridPublicKey(pub) {
  if (!pub || typeof pub !== 'object') {
    throw new TypeError('Invalid publicKey: must be {x25519, mlKem} object');
  }
  if (!(pub.x25519 instanceof Uint8Array) || pub.x25519.length !== KEY_SIZES.x25519PublicKey) {
    throw new Error('Invalid publicKey.x25519: must be 32 bytes');
  }
  if (!(pub.mlKem instanceof Uint8Array) || pub.mlKem.length !== KEY_SIZES.mlKemPublicKey) {
    throw new Error('Invalid publicKey.mlKem: must be 1184 bytes');
  }
}

function validateHybridPrivateKey(priv) {
  if (!priv || typeof priv !== 'object') {
    throw new TypeError('Invalid privateKey: must be {x25519, mlKem} object');
  }
  if (!(priv.x25519 instanceof Uint8Array) || priv.x25519.length !== KEY_SIZES.x25519PrivateKey) {
    throw new Error('Invalid privateKey.x25519: must be 32 bytes');
  }
  if (!(priv.mlKem instanceof Uint8Array) || priv.mlKem.length !== KEY_SIZES.mlKemPrivateKey) {
    throw new Error('Invalid privateKey.mlKem: must be 2400 bytes');
  }
}

/**
 * The combiner — pure: (KEM secrets, transcript, version) → 32-byte shared
 * secret. Exported so the binding property is testable DIRECTLY (the plan's
 * RED anchor: same two secrets, different transcript ⇒ different secret).
 *
 * @param {object} p
 * @param {string} p.version — KEX_V1 (deprecated, read-only) | KEX_V2
 * @param {Uint8Array} p.x25519Shared — 32 B X25519 ECDH output
 * @param {Uint8Array} p.mlKemShared — 32 B ML-KEM shared secret
 * @param {Uint8Array} p.kemCiphertext — 1088 B (v2 only)
 * @param {Uint8Array} p.x25519EphemeralPublic — 32 B (v2 only)
 * @param {{x25519: Uint8Array, mlKem: Uint8Array}} p.recipientPublicKey — (v2 only)
 * @returns {Uint8Array} 32-byte shared secret
 */
function combineSharedSecret({ version, x25519Shared, mlKemShared, kemCiphertext, x25519EphemeralPublic, recipientPublicKey }) {
  assertKexVersion(version);
  const label = kexHkdfLabel(version);
  assertBytes(x25519Shared, KEY_SIZES.sharedSecret, 'x25519Shared');
  assertBytes(mlKemShared, KEY_SIZES.sharedSecret, 'mlKemShared');

  if (version === KEX_V1) {
    // ⚠ DEPRECATED — v1 READ path only, kept so at-rest envelopes sealed
    // before T513 stay readable. Retired form, quoted verbatim from the
    // pre-T513 encapsulate/decapsulate (one commit, 5267d1f11):
    //   const combined = new Uint8Array(x25519Shared.length + mlKemShared.length);
    //   combined.set(x25519Shared, 0);
    //   combined.set(mlKemShared, x25519Shared.length);
    //   return hkdfSubkey(combined, 'oyster:kex:hybrid:v1', 32);
    // It binds NO transcript. Never select it for a NEW encapsulation —
    // encapsulate() hard-codes KEX_CURRENT and has no version parameter.
    return hkdfSubkey(concat([x25519Shared, mlKemShared]), label, KEY_SIZES.sharedSecret);
  }

  // v2 — X-Wing-style transcript binding. The field ORDER below is the wire
  // contract: the four built client bundles must produce the same bytes
  // (GOTCHA-355), and the pinned test recomputes it independently.
  //   ss_x25519 ‖ ss_mlkem ‖ kemCiphertext ‖ x25519EphemeralPublic ‖ pk_R.x25519 ‖ pk_R.mlKem
  assertBytes(kemCiphertext, KEY_SIZES.kemCiphertext, 'kemCiphertext');
  assertBytes(x25519EphemeralPublic, KEY_SIZES.x25519PublicKey, 'x25519EphemeralPublic');
  validateHybridPublicKey(recipientPublicKey);
  return hkdfSubkey(concat([x25519Shared, mlKemShared, kemCiphertext, x25519EphemeralPublic, recipientPublicKey.x25519, recipientPublicKey.mlKem]), label, KEY_SIZES.sharedSecret);
}

/**
 * Encapsulate a fresh shared secret to a recipient's hybrid public key.
 * Always the CURRENT combiner version (v2); there is deliberately no way to
 * ask for v1.
 *
 * @param {{x25519: Uint8Array, mlKem: Uint8Array}} recipientPublicKey
 * @returns {{kemCiphertext: Uint8Array, x25519EphemeralPublic: Uint8Array, sharedSecret: Uint8Array, kexVersion: string}}
 */
function encapsulate(recipientPublicKey) {
  validateHybridPublicKey(recipientPublicKey);

  // X25519 half: generate an ephemeral keypair and compute ECDH against recipient's pub
  const ephPriv = x25519.utils.randomSecretKey();
  const ephPub = x25519.getPublicKey(ephPriv);
  const x25519Shared = x25519.getSharedSecret(ephPriv, recipientPublicKey.x25519);

  // ML-KEM half: generate ciphertext targeting recipient's pub
  const kemResult = ml_kem768.encapsulate(recipientPublicKey.mlKem);

  // Combine via HKDF — both halves AND the transcript contribute (v2).
  const sharedSecret = combineSharedSecret({
    version: KEX_CURRENT,
    x25519Shared,
    mlKemShared: kemResult.sharedSecret,
    kemCiphertext: kemResult.cipherText,
    x25519EphemeralPublic: ephPub,
    recipientPublicKey,
  });
  ephPriv.fill(0);
  x25519Shared.fill(0);
  kemResult.sharedSecret.fill(0);

  return {
    kemCiphertext: kemResult.cipherText,
    x25519EphemeralPublic: ephPub,
    sharedSecret,
    kexVersion: KEX_CURRENT,
  };
}

/**
 * Decapsulate a shared secret from a hybrid ciphertext.
 *
 * @param {{x25519: Uint8Array, mlKem: Uint8Array}} privateKey
 * @param {Uint8Array} kemCiphertext — ML-KEM ciphertext from encapsulate()
 * @param {Uint8Array} x25519EphemeralPublic — sender's ephemeral pub from encapsulate()
 * @param {string} [kexVersion=KEX_CURRENT] — the version stamped on the
 *   envelope (algo-versions KEX_V1 | KEX_V2). Pass KEX_V1 ONLY for an at-rest
 *   envelope sealed before T513; a legacy envelope read under the default
 *   derives a different key and fails closed at the consumer's AEAD.
 * @returns {Uint8Array} 32-byte shared secret matching the encapsulating side
 */
function decapsulate(privateKey, kemCiphertext, x25519EphemeralPublic, kexVersion) {
  validateHybridPrivateKey(privateKey);
  if (!(kemCiphertext instanceof Uint8Array) || kemCiphertext.length !== KEY_SIZES.kemCiphertext) {
    throw new Error('Invalid kemCiphertext: must be ' + KEY_SIZES.kemCiphertext + ' bytes');
  }
  if (!(x25519EphemeralPublic instanceof Uint8Array) || x25519EphemeralPublic.length !== KEY_SIZES.x25519PublicKey) {
    throw new Error('Invalid x25519EphemeralPublic: must be 32 bytes');
  }
  const version = kexVersion === undefined ? KEX_CURRENT : kexVersion;
  assertKexVersion(version);
  // Applies to v1 reads too: no legitimate v1 envelope carries the bit (this
  // module's encapsulate never emitted one), so refusing costs nothing.
  assertCanonicalEphemeral(x25519EphemeralPublic);

  const x25519Shared = x25519.getSharedSecret(privateKey.x25519, x25519EphemeralPublic);
  const mlKemShared = ml_kem768.decapsulate(kemCiphertext, privateKey.mlKem);
  const recipientPublicKey = version === KEX_V1 ? undefined : {
    x25519: x25519.getPublicKey(privateKey.x25519),
    mlKem: mlKemPublicFromPrivate(privateKey.mlKem),
  };

  const sharedSecret = combineSharedSecret({
    version,
    x25519Shared,
    mlKemShared,
    kemCiphertext,
    x25519EphemeralPublic,
    recipientPublicKey,
  });
  x25519Shared.fill(0);
  mlKemShared.fill(0);
  return sharedSecret;
}

module.exports = {
  generateKeyPair,
  encapsulate,
  decapsulate,
  combineSharedSecret,
  KEY_SIZES,
  KEX_VERSIONS,
  KEX_V1,
  KEX_V2,
  KEX_CURRENT,
  KexError,
  ERR_KEX_NONCANONICAL_EPHEMERAL,
  ERR_KEX_VERSION_UNSUPPORTED,
};
